import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Bell, BellOff, Mail, Clock, Sparkles, Moon, Send, Loader2, Check, X, CheckCircle, AlertCircle, Smartphone } from 'lucide-react';
import { Button } from './ui/button';
import { Switch } from './ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import axios from 'axios';

import { BACKEND_URL } from '../lib/backend';

const API = `${BACKEND_URL}/api`;

// Helper function to convert URL-safe base64 to Uint8Array
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }

  return outputArray;
}

// Email Status Modal Component
const EmailStatusModal = ({ isOpen, onClose, status, message, details }) => {
  if (!isOpen) return null;
  
  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          onClick={(e) => e.stopPropagation()}
          className="cosmic-card w-full max-w-md p-6 text-center"
        >
          {/* Icon */}
          <div className={`w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center ${
            status === 'success' 
              ? 'bg-green-500/20' 
              : status === 'error' 
                ? 'bg-red-500/20' 
                : 'bg-[#C84B32]/20'
          }`}>
            {status === 'success' ? (
              <CheckCircle className="w-8 h-8 text-green-600" />
            ) : status === 'error' ? (
              <AlertCircle className="w-8 h-8 text-red-600" />
            ) : (
              <Loader2 className="w-8 h-8 text-[#C84B32] animate-spin" />
            )}
          </div>
          
          {/* Title */}
          <h3 className={`font-['Fraunces'] text-xl mb-2 ${
            status === 'success' ? 'text-green-600' : status === 'error' ? 'text-red-600' : 'text-[#C84B32]'
          }`}>
            {status === 'success' ? 'Email Sent Successfully!' : status === 'error' ? 'Failed to Send Email' : 'Sending Email...'}
          </h3>
          
          {/* Message */}
          <p className="text-[#6B6560] text-sm mb-4">{message}</p>
          
          {/* Details */}
          {details && status === 'success' && (
            <div className="bg-[#FFF9EF] rounded-lg p-4 mb-4 text-left">
              <div className="space-y-2 text-sm">
                {details.rashi && (
                  <div className="flex justify-between">
                    <span className="text-[#8A8279]">Your Rashi:</span>
                    <span className="text-[#C84B32] font-medium">{details.rashi}</span>
                  </div>
                )}
                {details.email_id && (
                  <div className="flex justify-between">
                    <span className="text-[#8A8279]">Email ID:</span>
                    <span className="text-[#6B6560] text-xs truncate max-w-[150px]">{details.email_id}</span>
                  </div>
                )}
                {details.horoscope_preview && (
                  <div className="mt-3 pt-3 border-t border-[#282622]/10">
                    <p className="text-[#8A8279] text-xs mb-1">Preview:</p>
                    <p className="text-[#282622] text-xs italic">"{details.horoscope_preview}"</p>
                  </div>
                )}
              </div>
            </div>
          )}
          
          {/* Close Button */}
          {status !== 'loading' && (
            <Button
              onClick={onClose}
              className={`w-full ${
                status === 'success' 
                  ? 'bg-green-500 hover:bg-green-600' 
                  : 'bg-[#C84B32] hover:bg-[#C84B32]/90'
              } text-white`}
            >
              {status === 'success' ? 'Check Your Inbox!' : 'Close'}
            </Button>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export const NotificationSettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [preferences, setPreferences] = useState({
    email_enabled: false,
    frequency: 'daily',
    transit_types: ['major'],
    include_predictions: true,
    include_remedies: true,
    preferred_time: '06:00'
  });
  
  // Push notification state
  const [pushSupported, setPushSupported] = useState(false);
  const [pushSubscription, setPushSubscription] = useState(null);
  const [pushLoading, setPushLoading] = useState(false);
  const [pushPermission, setPushPermission] = useState('default');
  
  // Email status modal state
  const [emailModal, setEmailModal] = useState({
    isOpen: false,
    status: 'loading', // 'loading', 'success', 'error'
    message: '',
    details: null
  });

  useEffect(() => {
    fetchPreferences();
    checkPushSupport();
  }, []);

  // Check if push notifications are supported
  const checkPushSupport = useCallback(async () => {
    const supported = 
      'serviceWorker' in navigator &&
      'PushManager' in window &&
      'Notification' in window;
    
    setPushSupported(supported);
    
    if (supported) {
      setPushPermission(Notification.permission);
      
      // Check existing subscription
      try {
        const registration = await navigator.serviceWorker.ready;
        const subscription = await registration.pushManager.getSubscription();
        setPushSubscription(subscription);
      } catch (error) {
        console.error('Error checking push subscription:', error);
      }
    }
  }, []);

  // Subscribe to push notifications
  const subscribeToPush = useCallback(async () => {
    setPushLoading(true);
    try {
      // Request notification permission
      const permission = await Notification.requestPermission();
      setPushPermission(permission);
      
      if (permission !== 'granted') {
        throw new Error('Notification permission denied');
      }
      
      // Get service worker registration
      const registration = await navigator.serviceWorker.ready;
      
      // Get VAPID public key from backend
      const vapidResponse = await axios.get(`${API}/push/vapid-public-key`, {
        withCredentials: true
      });
      const vapidPublicKey = vapidResponse.data.publicKey;
      
      // Subscribe to push
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidPublicKey)
      });
      
      // Send subscription to backend
      const subscriptionJson = subscription.toJSON();
      await axios.post(`${API}/push/subscribe`, {
        endpoint: subscriptionJson.endpoint,
        p256dh: subscriptionJson.keys.p256dh,
        auth: subscriptionJson.keys.auth
      }, { withCredentials: true });
      
      setPushSubscription(subscription);
      
      setEmailModal({
        isOpen: true,
        status: 'success',
        message: 'Push notifications enabled! You will now receive cosmic alerts on this device.',
        details: null
      });
      
    } catch (error) {
      console.error('Push subscription error:', error);
      setEmailModal({
        isOpen: true,
        status: 'error',
        message: error.message || 'Failed to enable push notifications',
        details: null
      });
    } finally {
      setPushLoading(false);
    }
  }, []);

  // Unsubscribe from push notifications
  const unsubscribeFromPush = useCallback(async () => {
    setPushLoading(true);
    try {
      if (pushSubscription) {
        // Notify backend
        await axios.post(`${API}/push/unsubscribe`, {
          endpoint: pushSubscription.endpoint
        }, { withCredentials: true });
        
        // Unsubscribe from push manager
        await pushSubscription.unsubscribe();
        setPushSubscription(null);
        
        setEmailModal({
          isOpen: true,
          status: 'success',
          message: 'Push notifications disabled for this device.',
          details: null
        });
      }
    } catch (error) {
      console.error('Push unsubscribe error:', error);
      setEmailModal({
        isOpen: true,
        status: 'error',
        message: 'Failed to disable push notifications',
        details: null
      });
    } finally {
      setPushLoading(false);
    }
  }, [pushSubscription]);

  // Send test push notification
  const sendTestPush = useCallback(async () => {
    setEmailModal({
      isOpen: true,
      status: 'loading',
      message: 'Sending test notification...',
      details: null
    });
    
    try {
      const response = await axios.post(`${API}/push/send-test`, {}, {
        withCredentials: true
      });
      
      setEmailModal({
        isOpen: true,
        status: 'success',
        message: response.data.message || 'Test notification sent!',
        details: { sent: response.data.sent }
      });
    } catch (error) {
      setEmailModal({
        isOpen: true,
        status: 'error',
        message: error.response?.data?.detail || 'Failed to send test notification',
        details: null
      });
    }
  }, []);

  const fetchPreferences = async () => {
    try {
      const response = await axios.get(`${API}/notifications/preferences`, {
        withCredentials: true
      });
      setPreferences(response.data);
    } catch (error) {
      console.error('Error fetching preferences:', error);
    } finally {
      setLoading(false);
    }
  };

  const savePreferences = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/notifications/preferences`, preferences, {
        withCredentials: true
      });
      setEmailModal({
        isOpen: true,
        status: 'success',
        message: 'Your notification preferences have been saved successfully!',
        details: null
      });
    } catch (error) {
      setEmailModal({
        isOpen: true,
        status: 'error',
        message: 'Failed to save preferences. Please try again.',
        details: null
      });
    } finally {
      setSaving(false);
    }
  };

  const sendTestTransitEmail = async () => {
    setEmailModal({
      isOpen: true,
      status: 'loading',
      message: 'Sending transit notification email...',
      details: null
    });
    
    try {
      const response = await axios.post(`${API}/transits/send-test-notification`, {}, {
        withCredentials: true
      });
      
      if (response.data.result?.success) {
        setEmailModal({
          isOpen: true,
          status: 'success',
          message: 'Transit notification email sent to your inbox!',
          details: {
            email_id: response.data.result.email_id,
            transits: `${response.data.transits_included} transits included`
          }
        });
      } else {
        throw new Error(response.data.result?.error || 'Failed to send');
      }
    } catch (error) {
      setEmailModal({
        isOpen: true,
        status: 'error',
        message: error.response?.data?.detail || 'Could not send transit email. Please try again.',
        details: null
      });
    }
  };

  const sendTestDailyEmail = async () => {
    setEmailModal({
      isOpen: true,
      status: 'loading',
      message: 'Generating your personalized daily horoscope...',
      details: null
    });
    
    try {
      const response = await axios.post(`${API}/horoscope/send-test-daily`, {}, {
        withCredentials: true
      });
      
      if (response.data.result?.success) {
        setEmailModal({
          isOpen: true,
          status: 'success',
          message: 'Your daily horoscope email has been sent!',
          details: {
            rashi: response.data.rashi,
            email_id: response.data.result.email_id,
            horoscope_preview: response.data.horoscope_preview
          }
        });
      } else {
        throw new Error(response.data.result?.error || 'Failed to send');
      }
    } catch (error) {
      setEmailModal({
        isOpen: true,
        status: 'error',
        message: error.response?.data?.detail || 'Could not send daily horoscope email. Please try again.',
        details: null
      });
    }
  };

  const handleToggleTransitType = (type) => {
    setPreferences(prev => {
      const types = prev.transit_types || [];
      if (types.includes(type)) {
        return { ...prev, transit_types: types.filter(t => t !== type) };
      } else {
        return { ...prev, transit_types: [...types, type] };
      }
    });
  };

  const closeModal = () => {
    setEmailModal(prev => ({ ...prev, isOpen: false }));
  };

  if (loading) {
    return (
      <div className="cosmic-card p-6 flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-[#C84B32]" />
      </div>
    );
  }

  return (
    <>
      {/* Email Status Modal */}
      <EmailStatusModal 
        isOpen={emailModal.isOpen}
        onClose={closeModal}
        status={emailModal.status}
        message={emailModal.message}
        details={emailModal.details}
      />
      
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="cosmic-card p-6"
      >
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#C84B32]/20 to-[#F5B942]/20 flex items-center justify-center">
            <Bell className="w-5 h-5 text-[#C84B32]" />
          </div>
          <div>
            <h3 className="font-['Fraunces'] text-lg text-[#282622]">Notifications</h3>
            <p className="text-[#8A8279] text-xs">Manage your cosmic alerts</p>
          </div>
        </div>

        <div className="space-y-5">
          {/* Push Notifications Section */}
          {pushSupported && (
            <div className="p-4 rounded-xl bg-gradient-to-r from-[#7C3AED]/10 to-[#F5B942]/10 border border-[#7C3AED]/20">
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#7C3AED] to-[#F5B942] flex items-center justify-center flex-shrink-0">
                    <Smartphone className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="text-[#282622] font-medium">Push Notifications</p>
                    <p className="text-[#6B6560] text-xs mt-1">
                      {pushSubscription 
                        ? 'Receiving cosmic alerts on this device' 
                        : 'Get instant alerts even when the app is closed'}
                    </p>
                    {pushPermission === 'denied' && (
                      <p className="text-red-600 text-xs mt-1">
                        Notifications blocked. Please enable in browser settings.
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex gap-2 flex-shrink-0">
                  {pushSubscription ? (
                    <>
                      <Button
                        onClick={sendTestPush}
                        size="sm"
                        variant="outline"
                        disabled={pushLoading}
                        className="border-[#C84B32]/50 text-[#C84B32] hover:bg-[#C84B32]/10"
                        data-testid="test-push-btn"
                      >
                        <Bell className="w-3.5 h-3.5 mr-1.5" />
                        Test
                      </Button>
                      <Button
                        onClick={unsubscribeFromPush}
                        size="sm"
                        variant="outline"
                        disabled={pushLoading}
                        className="border-red-500/50 text-red-600 hover:bg-red-500/10"
                        data-testid="disable-push-btn"
                      >
                        {pushLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <BellOff className="w-3.5 h-3.5" />}
                      </Button>
                    </>
                  ) : (
                    <Button
                      onClick={subscribeToPush}
                      size="sm"
                      disabled={pushLoading || pushPermission === 'denied'}
                      className="bg-gradient-to-r from-[#7C3AED] to-[#F5B942] hover:from-[#7C3AED]/90 hover:to-[#F5B942]/90 text-white"
                      data-testid="enable-push-btn"
                    >
                      {pushLoading ? (
                        <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                      ) : (
                        <Bell className="w-3.5 h-3.5 mr-1.5" />
                      )}
                      Enable
                    </Button>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Daily Horoscope Email - Main Feature */}
          <div className="p-4 rounded-xl bg-gradient-to-r from-[#EC4899]/10 to-[#F5B942]/10 border border-[#EC4899]/20">
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#EC4899] to-[#F5B942] flex items-center justify-center flex-shrink-0">
                  <Mail className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-[#282622] font-medium">Daily Horoscope Email</p>
                  <p className="text-[#6B6560] text-xs mt-1">Get your personalized daily prediction in your inbox every morning</p>
                </div>
              </div>
              <Button
                onClick={sendTestDailyEmail}
                size="sm"
                className="bg-gradient-to-r from-[#EC4899] to-[#F5B942] hover:from-[#EC4899]/90 hover:to-[#F5B942]/90 text-white flex-shrink-0"
                data-testid="send-daily-horoscope-btn"
              >
                <Send className="w-3.5 h-3.5 mr-1.5" />
                Send Me Today's
              </Button>
            </div>
          </div>

          {/* Transit Notifications Toggle */}
          <div className="p-4 rounded-xl bg-[#FFF9EF]">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {preferences.email_enabled ? (
                  <Bell className="w-5 h-5 text-[#C84B32]" />
                ) : (
                  <BellOff className="w-5 h-5 text-[#8A8279]" />
                )}
                <div>
                  <p className="text-[#282622] text-sm font-medium">Transit Alerts</p>
                  <p className="text-[#8A8279] text-xs">Get notified when planets change signs</p>
                </div>
              </div>
              <Switch
                checked={preferences.email_enabled}
                onCheckedChange={(checked) => setPreferences(prev => ({ ...prev, email_enabled: checked }))}
                data-testid="notification-toggle"
              />
            </div>
          </div>

          {preferences.email_enabled && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              className="space-y-4"
            >
              {/* Frequency Selection */}
              <div className="p-4 rounded-xl bg-[#FFF9EF]">
                <div className="flex items-center gap-2 mb-3">
                  <Clock className="w-4 h-4 text-[#C84B32]" />
                  <p className="text-[#282622] text-sm font-medium">Notification Frequency</p>
                </div>
                <Select
                  value={preferences.frequency}
                  onValueChange={(value) => setPreferences(prev => ({ ...prev, frequency: value }))}
                >
                  <SelectTrigger 
                    className="bg-white border-[#282622]/10 text-[#282622]"
                    data-testid="frequency-select"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-[#282622]/10">
                    <SelectItem value="realtime" className="text-[#282622]">Real-time (As transits happen)</SelectItem>
                    <SelectItem value="daily" className="text-[#282622]">Daily Digest (Morning)</SelectItem>
                    <SelectItem value="weekly" className="text-[#282622]">Weekly Summary</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Preferred Time */}
              {(preferences.frequency === 'daily' || preferences.frequency === 'weekly') && (
                <div className="p-4 rounded-xl bg-[#FFF9EF]">
                  <div className="flex items-center gap-2 mb-3">
                    <Clock className="w-4 h-4 text-[#C84B32]" />
                    <p className="text-[#282622] text-sm font-medium">Preferred Time (IST)</p>
                  </div>
                  <Select
                    value={preferences.preferred_time}
                    onValueChange={(value) => setPreferences(prev => ({ ...prev, preferred_time: value }))}
                  >
                    <SelectTrigger className="bg-white border-[#282622]/10 text-[#282622]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-[#282622]/10">
                      <SelectItem value="05:00" className="text-[#282622]">5:00 AM</SelectItem>
                      <SelectItem value="06:00" className="text-[#282622]">6:00 AM</SelectItem>
                      <SelectItem value="07:00" className="text-[#282622]">7:00 AM</SelectItem>
                      <SelectItem value="08:00" className="text-[#282622]">8:00 AM</SelectItem>
                      <SelectItem value="18:00" className="text-[#282622]">6:00 PM</SelectItem>
                      <SelectItem value="20:00" className="text-[#282622]">8:00 PM</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Transit Types */}
              <div className="p-4 rounded-xl bg-[#FFF9EF]">
                <div className="flex items-center gap-2 mb-3">
                  <Moon className="w-4 h-4 text-[#C84B32]" />
                  <p className="text-[#282622] text-sm font-medium">Transit Types</p>
                </div>
                <div className="flex flex-wrap gap-2">
                  {[
                    { id: 'all', label: 'All Transits', emoji: '🌟' },
                    { id: 'major', label: 'Major Only', emoji: '🔥' },
                    { id: 'personal', label: 'Personal', emoji: '✨' }
                  ].map(type => (
                    <button
                      key={type.id}
                      onClick={() => handleToggleTransitType(type.id)}
                      className={`px-3 py-2 rounded-lg text-xs font-medium transition-all ${
                        preferences.transit_types?.includes(type.id)
                          ? 'bg-[#C84B32] text-white'
                          : 'bg-white text-[#6B6560] hover:bg-[#FCE9D9]'
                      }`}
                    >
                      {type.emoji} {type.label}
                    </button>
                  ))}
                </div>
                <p className="text-[#8A8279] text-xs mt-2">
                  Major: Jupiter, Saturn, Rahu, Ketu | Personal: Based on your birth chart
                </p>
              </div>

              {/* Content Options */}
              <div className="p-4 rounded-xl bg-[#FFF9EF] space-y-3">
                <div className="flex items-center gap-2 mb-1">
                  <Sparkles className="w-4 h-4 text-[#C84B32]" />
                  <p className="text-[#282622] text-sm font-medium">Email Content</p>
                </div>
                
                <div className="flex items-center justify-between">
                  <span className="text-[#6B6560] text-sm">Include AI Predictions</span>
                  <Switch
                    checked={preferences.include_predictions}
                    onCheckedChange={(checked) => setPreferences(prev => ({ ...prev, include_predictions: checked }))}
                  />
                </div>
                
                <div className="flex items-center justify-between">
                  <span className="text-[#6B6560] text-sm">Include Remedies</span>
                  <Switch
                    checked={preferences.include_remedies}
                    onCheckedChange={(checked) => setPreferences(prev => ({ ...prev, include_remedies: checked }))}
                  />
                </div>
              </div>

              {/* Test Transit Email */}
              <div className="p-4 rounded-xl bg-[#FFF9EF]">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-[#282622] text-sm font-medium">Test Transit Alert</p>
                    <p className="text-[#8A8279] text-xs">Send a sample transit notification</p>
                  </div>
                  <Button
                    onClick={sendTestTransitEmail}
                    variant="outline"
                    size="sm"
                    className="border-[#C84B32]/50 text-[#C84B32] hover:bg-[#C84B32]/10"
                    data-testid="send-test-transit-btn"
                  >
                    <Send className="w-3.5 h-3.5 mr-1.5" />
                    Send Test
                  </Button>
                </div>
              </div>
            </motion.div>
          )}

          {/* Save Button */}
          <Button
            onClick={savePreferences}
            disabled={saving}
            className="w-full bg-gradient-to-r from-[#C84B32] to-[#E85D43] text-white hover:from-[#E85D43] hover:to-[#F5B942]"
            data-testid="save-preferences-btn"
          >
            {saving ? (
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
            ) : (
              <Check className="w-4 h-4 mr-2" />
            )}
            {saving ? 'Saving...' : 'Save Preferences'}
          </Button>
        </div>
      </motion.div>
    </>
  );
};

export default NotificationSettings;
